import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, switchMap, forkJoin, tap, map } from 'rxjs';
import { TransactionHistoryRequest } from '../../interfaces/transaction-history-request.interface';
import { TransactionHistoryList } from '../../interfaces/transaction-history-list';
import { environment } from '../../../environments/environment';
import { Announcement } from '../../interfaces/announcement.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../Auth/auth.service';
import { Products } from '../../interfaces/products.interface';
import { ValidationData } from '../../interfaces/validation-data.interface';
import { Transfer } from '../../interfaces/transfer.interface';
import { LicensesHistory } from '../../interfaces/licenses-history';
import { SiteIdResponse } from '../../interfaces/engage-siteID';
import {
  CompanySearch,
  TechSupportCompanySearch,
} from '../../interfaces/company-search.interface';
import { RevRec } from '../../interfaces/revrec.interface';
import { Profile } from '../../interfaces/profile';
import { BranchInventoryRequest } from '../../interfaces/branch-inventory-request.interface';
import { OnboardingPost } from '../../interfaces/onboarding-post.interface';
import { DDIGCompanySearchOnly } from '../../interfaces/DDIGCompanySearchOnly.interface';
import { OnboardingListTableData } from '../../interfaces/onboarding-list-table.interface';
import { MobileCredsLandingPageTable } from '../../interfaces/MobileCredsLandingPageTable.interface';
import { CPCMdetails } from '../../interfaces/CpCMdetails.interface';
import { CPdetails } from '../../interfaces/CPdetails.interface';
import { OverturSkus } from '../../interfaces/overturSKU.interface';
import {
  ComponentResponse,
  ProductFamilyResponse,
  ProductResponse,
  ZentraComponent,
} from '../../interfaces/subProduct.interface';
import {
  OverturAdminSubInfo,
  OverturSubscriptionResponse,
} from '../../interfaces/overtur-admin-subs';
import { OverturEndUserData } from '../../interfaces/overturEmailValidation.interface';
import { MaxioCustomer } from '../../interfaces/maxioCustomerInfo';
import { GuestPurchaserLandingResponse } from '../../interfaces/guest-purchaser-response.interface';
import { CredentialPricingItem } from '../../interfaces/credential-pricing.interface';
// CredentialPricingItem kept for the return type below

@Injectable({
  providedIn: 'root',
})
export class BackendAccessService {
  accessToken: string | undefined = '';
  tokenType: string | undefined = '';

  constructor(private http: HttpClient, private userAuth: AuthService) {
    this.userAuth.getTokenSilently$().subscribe((response) => {
      this.accessToken = response;
    });
  }

  headers = new HttpHeaders({
    'content-type': 'application/json',
    Authorization: `Bearer ${this.accessToken}`,
  });

  /* istanbul ignore next */
  private getHeaders(): Observable<HttpHeaders> {
    return this.userAuth.getTokenSilently$().pipe(
      map((token) => {
        return new HttpHeaders({
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        });
      })
    );
  }

  /* istanbul ignore next */
  getInventoryHistory(
    selectedBranch?: string | null
  ): Observable<LicensesHistory> {
    const httpUrl = environment.apiUrl2 + `/engagefixandflex/landing`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        // Construct the query parameters
        let params = new HttpParams();
        if (selectedBranch) {
          params = params.set('brid', selectedBranch);
        }

        const options = {
          headers: headers,
          params: params,
        };

        return this.http.get<LicensesHistory>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getRollup(selectedBranch?: string | null): Observable<any> {
    const httpUrl = environment.apiUrl2 + `/engagefixandflex/rollup`;

    return this.getHeaders().pipe(
      switchMap((headers) => {
        let params = new HttpParams();
        if (selectedBranch) {
          params = params.set('brid', selectedBranch);
        }

        const options = {
          headers: headers,
          params: params,
        };

        return this.http.get<any>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getSupportInventoryHistory(
    CompanySearch: CompanySearch
  ): Observable<LicensesHistory> {
    const httpUrl = environment.apiUrl2 + `/engagefixandflex/supportlanding`;
    const accid = CompanySearch.accountId;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const params = new HttpParams().set('accid', accid);

        const options = {
          headers: headers,
          params: params,
        };

        return this.http.get<LicensesHistory>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getSupportRollup(companySearch: CompanySearch): Observable<LicensesHistory> {
    const httpUrl = environment.apiUrl2 + `/engagefixandflex/supportrollup`;
    const accid = companySearch.accountId; // Assuming `accountId` is the GUID

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const params = new HttpParams().set('accid', accid);

        const options = {
          headers: headers,
          params: params,
        };

        return this.http.get<LicensesHistory>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getTransactionHistory(
    data: TransactionHistoryRequest
  ): Observable<TransactionHistoryList> {
    const httpUrl =
      environment.apiUrl2 + `/engagefixandflex/transactionhistory`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        // Convert data object to HttpParams
        let params = new HttpParams();
        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            params = params.set(key, data[key]);
          }
        }

        const options = {
          headers: headers,
          params: params,
        };

        return this.http.get<TransactionHistoryList>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  creditTransferPost(postData: ValidationData | Transfer, accountType: string) {
    let httpUrl: string;
    if (accountType === 'account') {
      httpUrl =
        environment.apiUrl2 + `/EngageFixandFlex/transferlicensetoaccount`;
    } else if (accountType === 'site') {
      httpUrl = environment.apiUrl2 + `/EngageFixandFlex/transferlicensetosite`;
    } else {
      throw new Error('Invalid account type');
    }

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };
        return this.http.post<any>(httpUrl, postData, options);
      })
    );
  }
  /* istanbul ignore next */
  creditSupportTransferPost(
    postData: ValidationData | Transfer,
    accountType: string
  ) {
    let httpUrl: string;
    if (accountType === 'account') {
      httpUrl =
        environment.apiUrl2 +
        `/EngageFixandFlex/transfersupportlicensetoaccount`;
    } else if (accountType === 'site') {
      httpUrl =
        environment.apiUrl2 + `/EngageFixandFlex/transfersupportlicensetosite`;
    } else {
      throw new Error('Invalid account type');
    }

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };
        return this.http.post<any>(httpUrl, postData, options);
      })
    );
  }

  // creditSupportTransferPost(
  //   postData: ValidationData | Transfer,
  //   accountType: string
  // ) {
  //   const httpUrl = environment.apiUrl + `/Credit/${accountType}/support`;

  //   return this.userAuth.getTokenSilently$().pipe(
  //     switchMap((token) => {
  //       const headers = new HttpHeaders({
  //         'Content-Type': 'application/json',
  //         Authorization: `Bearer ${token}`,
  //       });

  //       const options = {
  //         headers: headers,
  //       };
  //       return this.http.post<any>(httpUrl, postData, options);
  //     })
  //   );
  // }

  announcementPost(announcement: Announcement) {
    const httpUrl = environment.apiUrl + `/Announcements/Add`;
    const options = {
      headers: this.headers,
    };
    return this.http.post<any>(httpUrl, announcement, options);
  }

  /* istanbul ignore next */
  getProducts(): Observable<Products[]> {
    const httpUrl = `${environment.apiUrl2}/Products/getengageproducts`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        return this.http.get<Products[]>(httpUrl, { headers });
      })
    );
  }

  /* istanbul ignore next */
  getEngageIdName(siteId: string) {
    const httpUrl =
      environment.apiUrl2 + `/engageFixAndFlex/validatesiteid?siteid=${siteId}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };
        return this.http.get<SiteIdResponse>(httpUrl, options);
      })
    );
  }

  getCompanies(value: string = '') {
    const httpUrl = environment.apiUrl + `/Accounts`;
    const options = {
      headers: this.headers,
      params: { search: value },
    };
    return this.http.get<[CompanySearch]>(httpUrl, options);
  }

  /* istanbul ignore next */
  searchAccounts(value: string = ''): Observable<CompanySearch[]> {
    const httpUrl = environment.apiUrl2 + `/accounts`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const params = new HttpParams().set('acn', value);

        const options = {
          headers: headers,
          params: params,
        };

        return this.http.get<CompanySearch[]>(httpUrl, options);
      })
    );
  }

  getCompaniesRevRec() {
    const httpUrl =
      environment.apiUrl + `/Accounts/RevRecReport/GetAllAccounts`;
    const options = {
      headers: this.headers,
    };
    return this.http.get<[CompanySearch]>(httpUrl, options);
  }

  revRecReportPost(revrec: RevRec) {
    const httpUrl =
      environment.apiUrl + `/Report/RevenueRecognitionReport/Generate`;
    const options = {
      headers: this.headers,
    };
    return this.http.post<any>(httpUrl, revrec, options);
  }

  getProfileData(sub: string) {
    const httpUrl =
      environment.profileConfig.baseApiUrl + `/${encodeURIComponent(sub)}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };

        return this.http.get<Profile>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getBranchDetails(branchIds: unknown): Observable<any[]> {
    const httpUrl = environment.apiUrl2 + `/accounts/branchaccounts`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        // Ensure branchIds is an array
        let branchIdsArray: string[] = [];
        if (Array.isArray(branchIds)) {
          branchIdsArray = branchIds;
        } else if (typeof branchIds === 'string') {
          branchIdsArray = [branchIds];
        }

        // Create an array of HTTP GET observables
        const requests = branchIdsArray.map((branchId) => {
          let params = new HttpParams().set('brids', branchId);
          const options = {
            headers: headers,
            params: params,
          };
          return this.http.get<any>(httpUrl, options);
        });

        // Use forkJoin to execute all requests in parallel and flatten the result
        return forkJoin(requests).pipe(
          map((responses) => responses.flat()), // Flatten the array of arrays
          tap((flattenedResponses) =>
            console.log('Flattened responses:', flattenedResponses)
          )
        );
      })
    );
  }

  /*istanbul ignore next */
  getBranchDetailsByCompanyAccountId(
    companyAccountId: string
  ): Observable<any> {
    const httpUrl = environment.apiUrl2 + `/accounts/branchaccounts`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        // Construct the query parameters
        let params = new HttpParams();
        if (companyAccountId) {
          params = params.set('brids', companyAccountId);
        }

        const options = {
          headers: headers,
          params: params,
        };

        return this.http.get<any>(httpUrl, options);
      })
    );
  }

  //Onboarding
  getOnboardingAccountsSearch(value: string = '') {
    const httpUrl = environment.apiUrl2 + `/accounts/brand`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
          params: new HttpParams().set('acn', value),
        };
        return this.http.get<any>(httpUrl, options);
      })
    );
  }
  /* istanbul ignore next */
  submitOnboardingData(postData: OnboardingPost) {
    const httpUrl = environment.apiUrl2 + `/integrationsites`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };
        return this.http.post<[OnboardingPost]>(httpUrl, postData, options);
      })
    );
  }
  /* istanbul ignore next */
  getOnboardingListData() {
    const httpUrl = environment.apiUrl2 + `/integrationsites`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };
        return this.http.get<OnboardingListTableData[]>(httpUrl, options);
      })
    );
  }
  /* istanbul ignore next */
  deleteOnboardingData(accountSiteMappingId: string) {
    const httpUrl =
      environment.apiUrl2 + `/integrationsites/${accountSiteMappingId}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };
        return this.http.delete<{ isSuccess: boolean; errors: string | null }>(
          httpUrl,
          options
        );
      })
    );
  }
  /* istanbul ignore next */
  updateOnboardingData(accountSiteMappingId: string, patchData: any[]) {
    const httpUrl =
      environment.apiUrl2 + `/integrationsites/${accountSiteMappingId}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };
        return this.http.patch<any>(httpUrl, patchData, options);
      })
    );
  }

  // API Credentials
  getAPICredentialOverviewData(branchId: string) {
    const httpUrl = environment.apiUrl2 + `/bleCredentials/summaries`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        // Construct headers with the latest token
        const headers = new HttpHeaders({
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        // Add params to the options object
        const options = {
          headers: headers,
          params: {
            brid: branchId,
          },
        };

        return this.http.get<any>(httpUrl, options);
      })
    );
  }
  /* istanbul ignore next */
  getAPICredentialDetailsData(
    integrationId: string,
    skuCode: string,
    branchId: string
  ) {
    const httpUrl = environment.apiUrl2 + `/bleCredentials/monthlydetails`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        // Construct headers with the latest token
        const headers = new HttpHeaders({
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
          params: {
            intg: integrationId,
            prd: skuCode,
            brid: branchId,
          },
        };
        return this.http.get<any>(httpUrl, options);
      })
    );
  }
  /* istanbul ignore next */
  searchAccountsAPICreds(
    value: string = ''
  ): Observable<DDIGCompanySearchOnly[]> {
    const httpUrl = environment.apiUrl2 + `/accounts/techsupport`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        // Construct headers with the latest token
        const headers = new HttpHeaders({
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
          params: { acn: value },
        };

        return this.http.get<any>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getMobileCredsOverviewData(
    branchId: string
  ): Observable<MobileCredsLandingPageTable> {
    // Define the HTTP URL
    const httpUrl = environment.apiUrl2 + `/mobilecred/summaries`;

    // Get the latest access token dynamically
    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        // Construct headers with the latest token
        const headers = new HttpHeaders({
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        // Add params to the options object
        const options = {
          headers: headers,
          params: {
            brid: branchId,
          },
        };

        // Make the HTTP request with dynamic headers
        return this.http.get<MobileCredsLandingPageTable>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getCPCMDetailsData(clientId: string, branchId: string, mappingType: number) {
    const httpUrl = environment.apiUrl2 + `/mobilecredcpcm/monthlydetails`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        // Construct headers with the latest token
        const options = {
          headers: new HttpHeaders({
            'content-type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
          params: {
            clid: clientId,
            brid: branchId,
            mpt: mappingType.toString(), // Ensure mappingType is a string if your API expects it as such
          },
        };

        return this.http.get<CPCMdetails>(httpUrl, options);
      })
    );
  }
  /* istanbul ignore next */
  getCPDetailsData(clientId: string, branchId: string, mappingType: number) {
    const httpUrl = environment.apiUrl2 + `/mobilecredcponly/monthlydetails`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        // Construct headers with the latest token
        const options = {
          headers: new HttpHeaders({
            'content-type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
          params: {
            clid: clientId,
            brid: branchId,
            mpt: mappingType,
          },
        };

        return this.http.get<CPdetails>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  searchAccountsMobileCreds(
    value: string = ''
  ): Observable<DDIGCompanySearchOnly[]> {
    const httpUrl = environment.apiUrl2 + `/accounts/mobilecredtechsupport`;

    // Assuming `userAuth` is the service where `getTokenSilently$` is defined
    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        // Construct headers with the latest token
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
          params: { acn: value },
        };

        return this.http.get<DDIGCompanySearchOnly[]>(httpUrl, options);
      })
    );
  }
  /* istanbul ignore next */
  submitSupportForm(supportForm: any) {
    const httpUrl = environment.apiUrl2 + `/supportticket`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        // Create a FormData object
        const formData = new FormData();

        // Append the form fields to the FormData object. Adjust these keys according to your API's expected parameters
        formData.append('email', supportForm.userEmail);
        formData.append('subject', supportForm.subject);
        // Assuming the description already includes the product name, append it directly without adding the product name again
        formData.append('comment', supportForm.description);

        // If there are screenshots, append them to the FormData object
        supportForm.screenshots.forEach((file: File, index: number) => {
          formData.append(`files[${index}]`, file, file.name);
        });

        // Prepare the headers, note that 'Content-Type' is not set here. It's handled automatically by the browser when you use FormData.
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
          // You might need to include additional options depending on your API requirements
        };

        // Make the HTTP POST request
        return this.http.post<any>(httpUrl, formData, options);
      })
    );
  }

  //Subs
  /* istanbul ignore next */
  getSubProductFamily(): Observable<ProductFamilyResponse[]> {
    const httpUrl = `${environment.apiUrl2}/maxio/productfamilies`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<ProductFamilyResponse[]>(httpUrl, options);
      })
    );
  }

  // new
  /* istanbul ignore next */
  getSubProducts(productfamilyid: number): Observable<ProductResponse[]> {
    const httpUrl = `${environment.apiUrl2}/maxio/productfamilies/{productfamilyid}/products`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
          params: new HttpParams().set(
            'productfamilyid',
            productfamilyid.toString()
          ),
        };

        return this.http.get<ProductResponse[]>(httpUrl, options);
      })
    );
  }
  /* istanbul ignore next */
  getSubProductsWithKeyword(keyword: string): Observable<ProductResponse[]> {
    const httpUrl = `${environment.apiUrl2}/maxio/customers/${keyword}/products`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<ProductResponse[]>(httpUrl, options);
      })
    );
  }
  /* istanbul ignore next */
  getSubComponentsWithKeyword(keyword: string): Observable<ZentraComponent[]> {
    const httpUrl = `${environment.apiUrl2}/maxio/customers/${keyword}/components`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<ComponentResponse[]>(httpUrl, options);
      }),
      map((responses: ComponentResponse[]) =>
        responses.map((response) => response.component)
      )
    );
  }

  /* istanbul ignore next */
  getSubProductsWithKeywordIntegrator(
    keyword: string
  ): Observable<ProductResponse[]> {
    const httpUrl = `${environment.apiUrl2}/maxio/products/${keyword}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
          params: new HttpParams().set('rlcd', 'ZINT'),
        };

        return this.http.get<ProductResponse[]>(httpUrl, options);
      })
    );
  }
  /* istanbul ignore next */
  getSubProductsWithKeywordGuestPurchaser(
    keyword: string
  ): Observable<ProductResponse[]> {
    const httpUrl = `${environment.apiUrl2}/maxio/products/${keyword}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
          params: new HttpParams().set('rlcd', 'GPRC'),
        };

        return this.http.get<ProductResponse[]>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getSubComponentsWithKeywordIntegrator(
    keyword: string
  ): Observable<ZentraComponent[]> {
    const httpUrl = `${environment.apiUrl2}/maxio/components/${keyword}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
          params: new HttpParams().set('rlcd', 'ZINT'),
        };

        return this.http.get<ComponentResponse[]>(httpUrl, options);
      }),
      map((responses: ComponentResponse[]) =>
        responses.map((response) => response.component)
      )
    );
  }
  /* istanbul ignore next */
  getSubComponentsWithKeywordGuestPurchaser(
    keyword: string
  ): Observable<ZentraComponent[]> {
    const httpUrl = `${environment.apiUrl2}/maxio/components/${keyword}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
          params: new HttpParams().set('rlcd', 'GPRC'),
        };

        return this.http.get<ComponentResponse[]>(httpUrl, options);
      }),
      map((responses: ComponentResponse[]) =>
        responses.map((response) => response.component)
      )
    );
  }

  /* istanbul ignore next */
  submitConfiguredSubData(payload: any) {
    const httpUrl = environment.apiUrl2 + `/maxio/addsubscriptiondata`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        // Create a FormData object

        // Prepare the headers, note that 'Content-Type' is not set here. It's handled automatically by the browser when you use FormData.
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
          // You might need to include additional options depending on your API requirements
        };

        // Make the HTTP POST request
        return this.http.post<any>(httpUrl, payload, options);
      })
    );
  }

  /* istanbul ignore next */
  getPendingSubConfigurations(): Observable<any> {
    const httpUrl = `${environment.apiUrl2}/maxio/pendingsubscriptions`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<any>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getPOSubs(): Observable<any> {
    const httpUrl = `${environment.apiUrl2}/purchaseorder/subscriptions`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<any>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getPOSubById(recordId: string): Observable<any> {
    const httpUrl = `${environment.apiUrl2}/purchaseorder/subscription/${recordId}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<any>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  updatePOSubscription(payload: any): Observable<any> {
    const httpUrl = `${environment.apiUrl2}/purchaseorder/subscription/update`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };

        return this.http.post<any>(httpUrl, payload, options);
      })
    );
  }

  /* istanbul ignore next */
  getConfiguredSubByRecordId(recordId: string): Observable<any> {
    const httpUrl = `${environment.apiUrl2}/maxio/pendingsubscriptions/${recordId}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<any>(httpUrl, options);
      })
    );
  }
  /* istanbul ignore next */
  submitEditedConfiguredSubData(editedPayload: any) {
    const httpUrl = environment.apiUrl2 + `/maxio/updatependingsubscription`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        // Create a FormData object

        // Prepare the headers, note that 'Content-Type' is not set here. It's handled automatically by the browser when you use FormData.
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
          // You might need to include additional options depending on your API requirements
        };

        // Make the HTTP POST request
        return this.http.post<any>(httpUrl, editedPayload, options);
      })
    );
  }

  /* istanbul ignore next */
  deleteSubConfigurations(recordId: any): Observable<any> {
    const httpUrl = `${environment.apiUrl2}/maxio/pendingsubscriptions/${recordId}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.delete<any>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  sendInvite(users: any) {
    const httpUrl = environment.apiUrl2 + `/maxio/invite`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };

        return this.http.post<any>(httpUrl, users, options);
      })
    );
  }

  /* istanbul ignore next */
  getSubConfigsActiveSubsZentraEndUser(): Observable<any> {
    const httpUrl = `${environment.apiUrl2}/maxio/user/landing`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<any>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  previewSubscription(payload: any) {
    const httpUrl = environment.apiUrl2 + `/maxio/subscription/preview`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };

        return this.http.post<any>(httpUrl, payload, options);
      })
    );
  }

  /* istanbul ignore next */
  createSubscription(subPayload: any) {
    const httpUrl = environment.apiUrl2 + `/maxio/createsubscription`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };

        return this.http.post<any>(httpUrl, subPayload, options); // Send the full payload
      })
    );
  }
  /* istanbul ignore next */
  createSubscriptionGP(subPayload: any) {
    return this.createSubscription(subPayload);
  }

  /* istanbul ignore next */
  updateWholesaler(subscriptionId: string, futureWholesalerUserEmail: any) {
    const httpUrl = environment.apiUrl2 + `/maxio/subscription/updatewholesaler`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };

        return this.http.post<any>(
          httpUrl,
          { subscriptionId, futureWholesalerUserEmail, roleCode: 'GPRC' },
          options
        );
      })
    );
  }

  /* istanbul ignore next */
  createSubscriptionIntegrator(subPayload: any) {
    return this.createSubscriptionGP(subPayload);
  }

  /* istanbul ignore next */
  getAllSubsListPage(
    roleId: string,
    pageNo: number,
    ItemsPerPage: number
  ): Observable<any> {
    const httpUrl = `${environment.apiUrl2}/maxio/${roleId}/subscriptions?pno=${pageNo}&iperpage=${ItemsPerPage}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<any>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getIndividualSubDetails(maxioSubId: string): Observable<any> {
    const httpUrl = `${environment.apiUrl2}/maxio/subscription/${maxioSubId}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<any>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  addPaymentMethod(payload: any) {
    const httpUrl = `${environment.apiUrl2}/maxio/subscription/updatepaymentmethod`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };

        return this.http.post<any>(httpUrl, payload, options); // Send the full payload
      })
    );
  }

  /* istanbul ignore next */
  getMaxioCustomerList(email: string): Observable<MaxioCustomer[]> {
    const httpUrl = `${environment.apiUrl2}/maxio/customers?emailId=${email}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<MaxioCustomer[]>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  signupInvoicePreview(subPayload: any) {
    const httpUrl = `${environment.apiUrl2}/maxio/subscription/previewsignupinvoice`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };

        return this.http.post<any>(httpUrl, subPayload, options); // Send the full payload
      })
    );
  }

  /* istanbul ignore next */
  deleteGuestPurchaser(permissionId: string) {
    const httpUrl = `${environment.apiUrl2}/maxio/userpermissions/${permissionId}`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };

        return this.http.delete<any>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getGuestPurchaserUsersZentra(
    pno: number,
    iperpage: number
  ): Observable<GuestPurchaserLandingResponse> {
    const httpUrl = `${environment.apiUrl2}/maxio/userpermissions`;

    // Use HttpParams to safely construct the query string
    const params = new HttpParams()
      .set('pno', pno.toString())
      .set('iperpage', iperpage.toString());

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
          params: params, // Attach the query parameters here
        };

        return this.http.get<GuestPurchaserLandingResponse>(httpUrl, options);
      })
    );
  }

  // overtur
  /* istanbul ignore next */
  getOverturSubListLanding(): Observable<OverturAdminSubInfo[]> {
    const httpUrl = `${environment.apiUrl2}/OverturSubscriptions/admin/subscriptions`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<OverturAdminSubInfo[]>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getOverturSubSkus(): Observable<OverturSkus[]> {
    const httpUrl = `${environment.apiUrl2}/products/overturtrialproducts`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<OverturSkus[]>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getOverturEmailValidation(email: string): Observable<OverturEndUserData> {
    const httpUrl = `${environment.apiUrl2}/overtursubscriptions/admin/enduseremail/${email}/validate`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<OverturEndUserData>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  submitOverturAdminTrial(payload: any) {
    const httpUrl = `${environment.apiUrl2}/overtursubscriptions/admin/trialsubscription/create`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };

        return this.http.post<any>(httpUrl, payload, options);
      })
    );
  }

  /* istanbul ignore next */
  deleteOverturAdminTrial(payload: any) {
    const httpUrl = `${environment.apiUrl2}/overtursubscriptions/admin/trialsubscription/delete`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };

        return this.http.post<any>(httpUrl, payload, options);
      })
    );
  }

  /* istanbul ignore next */
  getOverturSubscriptionDetails(payload: {
    orid: string;
    subid?: string;
  }): Observable<OverturSubscriptionResponse> {
    const httpUrl = `${environment.apiUrl2}/overtursubscriptions/admin/subscription`;

    let params = new HttpParams().set('orid', payload.orid);
    if (payload.subid) {
      params = params.set('subid', payload.subid);
    }

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
          params,
        };

        return this.http.get<OverturSubscriptionResponse>(httpUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  updateOverturAdminSubs(payload: any) {
    const httpUrl = `${environment.apiUrl2}/overtursubscriptions/admin/subscription/update`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        const options = {
          headers: headers,
        };

        return this.http.post<any>(httpUrl, payload, options);
      })
    );
  }

  /* istanbul ignore next */
  getCredentialPricing(): Observable<CredentialPricingItem[]> {
    const httpUrl = `${environment.apiUrl2}/integrationsites/credentialpricing`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        };

        return this.http.get<CredentialPricingItem[]>(httpUrl, options);
      })
    );
  }
}
